/// <amd-module name="Dashboards/Directory/redux/DashboardsDirectorySelectors" />

import { DashboardScope } from "Dashboards/Common/Contracts/Dashboard";
import { IFavoriteData } from "VSS/Features/Directory/DirectoryContracts";
import { IUnifiedDirectoryDataProviderData } from "VSS/Features/ReduxDirectory/Contracts";
import { Dashboard, IDashboardsDirectoryArtifact, IDashboardsDirectoryAwareState, Owner } from "../DashboardsDirectoryContracts";

/**
 * Takes the flat dashboards data and creates a Unified directory state for consumption by the Redux Directory.
 * @param state a state object consisting of all of the flat data from dashboards data providers.
 */
export function getUnifiedDirectoryData(state: IDashboardsDirectoryAwareState): IUnifiedDirectoryDataProviderData<IDashboardsDirectoryArtifact> {
    const myDashboards = state.dashboardsDirectoryState.directoryArtifactState.myDashboards || [];
    const myOwners = state.dashboardsDirectoryState.directoryArtifactState.myOwners || [];
    const favorites = state.dashboardsDirectoryState.directoryArtifactState.favorites || [];
    const mru = state.dashboardsDirectoryState.directoryArtifactState.mruDashboard || {};

    const favoriteDashboards = favorites.map(f => f.dashboard);
    const favoriteOwners = favorites.map(f => f.owner);

    const dashboards = union(myDashboards, favoriteDashboards);
    const owners = union(myOwners, favoriteOwners);

    const directoryArtifacts = createDashboardDirectoryArtifacts(dashboards, owners);

    if (mru) {
        const mruArtifact = createDashboardDirectoryArtifact(mru.dashboard, [mru.owner]);
        mruArtifact && directoryArtifacts.push(mruArtifact);
    }

    const customGroupings: { [key: string]: string[] } = {};
    myOwners.map(owner => {
        customGroupings[owner.name] = dashboards.filter(db => owner.id === db.ownerId).map(db => db.id);
    });

    const unifiedFavorites: IFavoriteData[] =
        favorites.length > 0
            ? favorites.map(favoriteDashboard => {
                const { dashboard, favorite } = favoriteDashboard;
                return {
                    id: favorite.id,
                    artifactId: dashboard.id,
                    artifactName: dashboard.name,
                    artifactProperties: {}, // Leaving empty
                    isDeleted: favorite.artifactIsDeleted
                };
            })
            : [];

    const recentKeys: string[] = [];
    mru.dashboard && recentKeys.push(mru.dashboard.id);

    return {
        artifacts: directoryArtifacts,
        customGroupings: customGroupings,
        favorites: unifiedFavorites,
        recentKeys: recentKeys
    };
}

export function getDashboardDirectoryArtifact(state: IDashboardsDirectoryAwareState, dashboard: Dashboard): IDashboardsDirectoryArtifact | null {
    const myOwners = state.dashboardsDirectoryState.directoryArtifactState.myOwners || [];
    const favorites = state.dashboardsDirectoryState.directoryArtifactState.favorites || [];
    const mru = state.dashboardsDirectoryState.directoryArtifactState.mruDashboard || {};

    const favoriteOwners = favorites.map(f => f.owner);
    const owners = union([mru.owner], myOwners, favoriteOwners);

    return createDashboardDirectoryArtifact(dashboard, owners);
}

/**
 * Takes the flat dashboards state for all dashboards and owners and tranforms them into
 * a list of artifacts for use by the redux directory.
 */
export function getAllDirectoryData(state: IDashboardsDirectoryAwareState): IDashboardsDirectoryArtifact[] {
    const allDashboards = state.dashboardsDirectoryState.directoryArtifactState.allDashboards || [];
    const allOwners = state.dashboardsDirectoryState.directoryArtifactState.allOwners || [];
    return createDashboardDirectoryArtifacts(allDashboards, allOwners);
}

export function getCanCreateDashboardsPermission(state: IDashboardsDirectoryAwareState): boolean {
    return state.dashboardsDirectoryState.directoryArtifactState.canCreateDashboardsPermission;
}

export function getCanManageDashboardSecurityPermission(state: IDashboardsDirectoryAwareState): boolean {
    return state.dashboardsDirectoryState.directoryArtifactState.canManageDashboardsSecurityPermission;
}

export function getCanDeleteDashboardsPermission(state: IDashboardsDirectoryAwareState): boolean {
    return state.dashboardsDirectoryState.directoryArtifactState.canDeleteDashboardsPermission;
}

export function getCanUseTeamButtonPermission(state: IDashboardsDirectoryAwareState): boolean {
    return state.dashboardsDirectoryState.directoryArtifactState.canUseTeamPanelPermission;
}

/**
 * Reduces an array of dashboards and an array of owners into an array of directory artifacts.
 * Dashboards for which no owner is found will be omitted from the return array.
 */
function createDashboardDirectoryArtifacts(dashboards: Dashboard[], owners: Owner[]): IDashboardsDirectoryArtifact[] {
    const artifacts = dashboards.reduce(
        (directoryArtifacts, dashboard) => {
            const directoryArtifact = createDashboardDirectoryArtifact(dashboard, owners);
            if (directoryArtifact != null) {
                directoryArtifacts.push(directoryArtifact);
            }
            return directoryArtifacts;
        },
        [] as IDashboardsDirectoryArtifact[]
    );

    return artifacts;
}

/**
 * Tries to create a DashboardDirectoryArtifact. If the dashboard's owner cannot be found in the list of owners, null will be returned.
 */
function createDashboardDirectoryArtifact(dashboard: Dashboard, owners: Owner[]): IDashboardsDirectoryArtifact | null {
    if (!dashboard || !owners.length) {
        return null;
    }

    const dashboardOwners = owners
        .filter(o => o.id === dashboard.ownerId)
        .map(owner => {
            return {
                id: owner.id,
                name: owner.name,
                imageUrl: owner.imageUrl,
                isTeam: true
            };
        });

    const dashboardModifiedBy = owners
        .filter(o => o.id === dashboard.modifiedBy)
        .map(owner => {
            return {
                id: owner.id,
                name: owner.name,
                imageUrl: owner.imageUrl,
                isTeam: true
            };
        });

    // filter out team dashboard associated with a team that does not exist
    if (dashboard.dashboardScope === DashboardScope.Project_Team && dashboardOwners.length === 0) {
        return null;
    }

    let ownerName = "";
    let ownerImageUrl = "";

    if (dashboardOwners.length) {
        ownerName = dashboardOwners[0].name;
        ownerImageUrl = dashboardOwners[0].imageUrl;
    }

    let modifiedByName = "";
    let modifiedByImageUrl = "";

    if(dashboardModifiedBy.length){
        modifiedByName = dashboardModifiedBy[0].name;
        modifiedByImageUrl = dashboardModifiedBy[0].imageUrl;
    }

    const directoryArtifact = {
        id: dashboard.id,
        name: dashboard.name,
        description: dashboard.description,
        url: "", // Set later in the saga
        owners: dashboardOwners,
        ownerName: ownerName,
        ownerImageUrl: ownerImageUrl,
        dashboardScope: dashboard.dashboardScope,
        lastAccessedDate: dashboard.lastAccessedDate,
        modifiedBy: dashboard.modifiedBy,
        modifiedDate: dashboard.modifiedDate,
        modifiedByName: modifiedByName,
        modifiedByImageUrl: modifiedByImageUrl
    };

    return directoryArtifact;
}

/**
 * Combines 2 or more arrays into a single array, removes elements with duplicate id fields.
 */
function union<T extends { id: string }>(...arrays: T[][]): T[] {
    let combinedArrays: T[] = ([] as T[]).concat(...arrays);
    combinedArrays = combinedArrays.filter(t => t != null);

    const tMap: { [key: string]: T } = {};
    combinedArrays.forEach(t => (tMap[t.id] = t));
    combinedArrays = [];
    Object.keys(tMap).forEach(d => {
        combinedArrays.push(tMap[d]);
    });

    return combinedArrays;
}
